import { companyPreregistrationOptions } from "@/api";
import PageBackButton from "@/components/PageBackButton";
import { PreRegsCalendar } from "@/components/pre-registration/FuturePreRegsCalendar";
import { useCompanyAdminAccess } from "@/hooks/useCompanyAdminAccess";
import { useWelcomeVisitAccess } from "@/hooks/useWelcomeVisitAccess";
import { WELCOME_VISITORS_URL } from "@/lib";
import { useRouterStore } from "@/store/zustandStore";
import { PreRegCalendarEvent, PreRegistrationGroup } from "@/types";
import { Breadcrumbs } from "@app-components";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { addHours } from "date-fns";
import PreregistrationDetailsModal from "@/components/pre-registration/PreregistrationDetailsModal";
import { useBuildingQuery } from "@/components/RouterSetup";

export const Route = createFileRoute(
  "/_portal/company/$companyId/visitors/preregistrations/",
)({
  component: RouteComponent,
  loader: ({ params }) => {
    const { queryClient } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(
      companyPreregistrationOptions(params.companyId, "future"),
    );
  },
});

function RouteComponent() {
  const { t, i18n } = useTranslation();
  useWelcomeVisitAccess();
  const navigate = useNavigate();
  const { companyId } = Route.useParams();
  const { allowCompanyAdminAccess } = useCompanyAdminAccess(companyId);
  const { data: preregistrationGroups } = useBuildingQuery<
    PreRegistrationGroup[]
  >(companyPreregistrationOptions(companyId ?? "", "future"));
  const [selectedGroup, setSelectedGroup] = useState<PreRegCalendarEvent>();
  const calendarEvents = useMemo<PreRegCalendarEvent[]>(
    () =>
      preregistrationGroups?.map((group) => ({
        ...group,
        start: new Date(group.startTime._seconds * 1000),
        end: addHours(new Date(group.startTime._seconds * 1000), 2),
        title: group.eventName,
      })) ?? [],
    [preregistrationGroups],
  );

  const breadcrumbs = [
    {
      label: t("SIDEBAR.COMPANY_VISITORS"),
      path: `/company/${companyId}/visitors`,
    },
    {
      label: t("BUILDING_VISITORS.PRE_REG"),
      path: `/company/${companyId}/visitors/preregistrations`,
    },
  ];

  if (!allowCompanyAdminAccess) {
    return null;
  }
  return (
    <>
      <div className="backAndBreadcrumbsContainer">
        <PageBackButton fallbackPath={{ to: breadcrumbs[0].path }} />
        <Breadcrumbs
          levels={breadcrumbs}
          onNavigate={(path) => navigate({ to: path })}
        />
      </div>
      <div className="pageHeader">
        <h1>{t("PAGES.FUTURE_COMPANY_VISITORS.HEADER")}</h1>
        <p>
          <Trans i18nKey="PAGES.FUTURE_COMPANY_VISITORS.SUBHEADER">
            Text
            <a href={WELCOME_VISITORS_URL(i18n.language)} target="_blank"></a>.
          </Trans>
        </p>
      </div>
      <PreRegsCalendar
        events={calendarEvents}
        onEventClick={(group) => setSelectedGroup(group)}
      />
      {!!selectedGroup && (
        <PreregistrationDetailsModal
          onClose={() => setSelectedGroup(undefined)}
          prereg={selectedGroup}
          companyAdminMode
          open={!!selectedGroup}
        />
      )}
    </>
  );
}
